import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PortDetailsDto } from "~/api/GeneratedApi";
import { CesiumMapItem } from "~/pages/MapPage/3d/types/CesiumMapItems";

export type MapType = "2d" | "3d";
export type CesiumState = "unloaded" | "loading" | "ready";

export type CesiumTool = "simpleMeasurement" | "measurement" | "geopicking";

interface MapState {
  mapType: MapType;
  cesiumState: CesiumState;
  cesiumPreloadMeas?: {
    mapId: string;
    measId: number;
  };
  hoveredCesiumItem?: CesiumMapItem;
  selectedCesiumItem?: CesiumMapItem;
  cesiumPopupPosition:
    | {
        bottom: number;
        left: number;
      }
    | undefined;
  cesiumPrimitivesChanged: boolean;
  selectedPointclouds?: number[];
  loadingTilesets: number[];
  activeCesiumTool?: CesiumTool;
  focusedPort?: PortDetailsDto;
  cesiumCoordinates?: string;
  terrainAdjustmentDialogOpen: boolean;
}

const initialState: MapState = {
  mapType: "2d",
  cesiumState: "unloaded",
  hoveredCesiumItem: undefined,
  selectedCesiumItem: undefined,
  cesiumPopupPosition: undefined,
  cesiumPrimitivesChanged: false,
  cesiumPreloadMeas: undefined,
  selectedPointclouds: undefined,
  loadingTilesets: [],
  activeCesiumTool: undefined,
  focusedPort: undefined,
  cesiumCoordinates: undefined,
  terrainAdjustmentDialogOpen: false,
};

export const generalSlice = createSlice({
  name: "map",
  initialState,
  reducers: {
    setMapType: (state, action: PayloadAction<MapType>) => {
      state.mapType = action.payload;
    },
    setCesiumState: (state, action: PayloadAction<CesiumState>) => {
      state.cesiumState = action.payload;
    },
    setCesiumPreloadMeas: (
      state,
      action: PayloadAction<{ mapId: string; measId: number } | undefined>,
    ) => {
      state.cesiumPreloadMeas = action.payload;
    },
    setHoveredCesiumItem: (
      state,
      action: PayloadAction<CesiumMapItem | undefined>,
    ) => {
      state.hoveredCesiumItem = action.payload;
    },
    setSelectedCesiumItem: (
      state,
      action: PayloadAction<CesiumMapItem | undefined>,
    ) => {
      state.selectedCesiumItem = action.payload;
    },
    setCesiumPopupPosition: (
      state,
      action: PayloadAction<{ bottom: number; left: number } | undefined>,
    ) => {
      state.cesiumPopupPosition = action.payload;
    },
    toggleCesiumPrimitivesChanged: (state) => {
      state.cesiumPrimitivesChanged = !state.cesiumPrimitivesChanged;
    },
    setSelectedPointcloud: (
      state,
      action: PayloadAction<number[] | undefined>,
    ) => {
      state.selectedPointclouds = action.payload;
    },
    addLoadingTileset: (state, action: PayloadAction<number>) => {
      state.loadingTilesets = [...state.loadingTilesets, action.payload];
    },
    removeLoadingTileset: (state, action: PayloadAction<number>) => {
      state.loadingTilesets = state.loadingTilesets.filter(
        (t) => t !== action.payload,
      );
    },
    setActiveCesiumTool: (
      state,
      action: PayloadAction<CesiumTool | undefined>,
    ) => {
      state.activeCesiumTool = action.payload;
    },
    setFocusedPort: (
      state,
      action: PayloadAction<PortDetailsDto | undefined>,
    ) => {
      state.focusedPort = action.payload;
    },
    setCesiumCoordinates: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.cesiumCoordinates = action.payload;
    },
    setTerrainAdjustmentDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.terrainAdjustmentDialogOpen = action.payload;
    },
  },
});

export const {
  setMapType,
  setCesiumState,
  setCesiumPreloadMeas,
  setHoveredCesiumItem,
  setSelectedCesiumItem,
  setCesiumPopupPosition,
  toggleCesiumPrimitivesChanged,
  setSelectedPointcloud,
  addLoadingTileset,
  removeLoadingTileset,
  setActiveCesiumTool,
  setFocusedPort,
  setCesiumCoordinates,
  setTerrainAdjustmentDialogOpen,
} = generalSlice.actions;

export default generalSlice.reducer;
